/**
 * Standings Table - Points table with NRR.
 * Events with a rulebook show "How points work" from it, half points for a
 * shared tie, and "Level — to be decided" where a super over / bowl-out / toss
 * is the next tie-breaker (admins record who won).
 */
import { useEffect, useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { adjustmentsApi } from '../rulebook/api';
import HowPointsWork from '../rulebook/HowPointsWork';
import DeciderSheet, { deciderWord } from '../rulebook/DeciderSheet';
import { toast } from 'sonner';

const fmtPoints = (n) => (Number.isInteger(n) ? n : Number((n || 0).toFixed(2)));

export default function StandingsTable({ standings, pointConfig, standingsRules, isAdmin = false, eventId, onRefresh }) {
  const [rebuilding, setRebuilding] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const managed = !!standingsRules?.managed;
  // Points given or taken away by the organiser or umpire (rulebook adjustments)
  const [pointAdjustments, setPointAdjustments] = useState([]);
  useEffect(() => {
    if (!eventId) return;
    adjustmentsApi.list(eventId).then((all) => setPointAdjustments(all.filter((a) => a.kind === 'points'))).catch(() => {});
  }, [eventId, standings]);

  const handleRebuild = async () => {
    if (!eventId) return;
    setRebuilding(true);
    try {
      const res = await axiosInstance.post(`/v1/events/${eventId}/fixtures/standings/rebuild`);
      toast.success(`Standings rebuilt (${res.data?.count || 0} teams)`);
      if (onRefresh) await onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to rebuild standings');
    } finally {
      setRebuilding(false);
    }
  };

  if (!standings || standings.length === 0) {
    return (
      <div className="text-center py-12" data-testid="standings-empty">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <i className="ph-bold ph-ranking text-2xl text-gray-400"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-700 mb-2">No Standings Yet</h3>
        <p className="text-sm text-gray-500">
          Standings will appear here once matches are completed.
        </p>
        {isAdmin && eventId && (
          <button
            onClick={handleRebuild}
            disabled={rebuilding}
            data-testid="rebuild-standings-btn"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#3AAFA9] text-white text-sm font-semibold hover:bg-[#2D8A85] disabled:opacity-50"
          >
            <i className={`ph-bold ${rebuilding ? 'ph-spinner animate-spin' : 'ph-arrows-clockwise'}`}></i>
            {rebuilding ? 'Rebuilding…' : 'Rebuild Standings'}
          </button>
        )}
      </div>
    );
  }

  // Group standings by group_name
  const groupedStandings = standings.reduce((acc, standing) => {
    const group = standing.group_name || 'League';
    if (!acc[group]) acc[group] = [];
    acc[group].push(standing);
    return acc;
  }, {});

  // Sort each group by position
  Object.keys(groupedStandings).forEach(group => {
    groupedStandings[group].sort((a, b) => a.position - b.position);
  });

  const formatNRR = (nrr) => {
    if (!nrr) return '0.000';
    return nrr >= 0 ? `+${nrr.toFixed(3)}` : nrr.toFixed(3);
  };

  const getPositionColor = (position) => {
    if (position === 1) return 'bg-amber-100 text-amber-700';
    if (position === 2) return 'bg-gray-100 text-gray-700';
    if (position <= 4) return 'bg-[#3AAFA9]/10 text-[#3AAFA9]';
    return 'bg-gray-50 text-gray-500';
  };

  return (
    <div className="space-y-6" data-testid="standings-table">
      {managed && <HowPointsWork rules={standingsRules} canEdit={isAdmin} />}

      {/* Point System Info + Rebuild (admin) */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {pointConfig && !managed && (
          <div className="bg-gray-50 rounded-xl p-3 flex flex-wrap items-center gap-4 text-xs text-gray-600 flex-1 min-w-0">
            <span className="font-medium text-gray-700">Point System:</span>
            <span>Win: <strong>{pointConfig.points_win}</strong></span>
            <span>Loss: <strong>{pointConfig.points_loss}</strong></span>
            <span>Tie: <strong>{pointConfig.points_tie}</strong></span>
            <span>NR: <strong>{pointConfig.points_no_result}</strong></span>
          </div>
        )}
        {isAdmin && eventId && (
          <button
            onClick={handleRebuild}
            disabled={rebuilding}
            data-testid="rebuild-standings-btn"
            title="Re-derive standings from completed fixtures"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-gray-200 text-xs font-semibold text-[#2B3A67] hover:border-[#3AAFA9] hover:text-[#3AAFA9] disabled:opacity-50 shrink-0"
          >
            <i className={`ph-bold ${rebuilding ? 'ph-spinner animate-spin' : 'ph-arrows-clockwise'}`}></i>
            {rebuilding ? 'Rebuilding…' : 'Rebuild'}
          </button>
        )}
      </div>

      {/* Standings Tables */}
      {Object.entries(groupedStandings).map(([group, teams]) => (
        <div key={group} className="bg-white border border-gray-100 rounded-xl overflow-hidden">
          {/* Group Header */}
          <div className="px-4 py-3 bg-[#2B3A67] text-white font-bold flex items-center gap-2">
            <i className="ph-bold ph-ranking"></i>
            {group === 'League' ? 'League Standings' : `Group ${group}`}
          </div>

          {/* Table — numerics tight, team name wraps (never truncates) */}
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-50 text-gray-500 text-[10px] sm:text-xs uppercase">
              <tr>
                <th className="pl-2 sm:pl-3 pr-1 py-2 text-left">Team</th>
                <th className="px-1 sm:px-2 py-2 text-center">P</th>
                <th className="px-1 sm:px-2 py-2 text-center">W</th>
                <th className="px-1 sm:px-2 py-2 text-center">L</th>
                <th className="px-1 sm:px-2 py-2 text-center">T</th>
                <th className="px-1 sm:px-2 py-2 text-center">NR</th>
                <th className="px-1 sm:px-2 py-2 text-center font-bold">Pts</th>
                <th className="pl-1 pr-2 sm:pr-3 py-2 text-right">NRR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {teams.map((team, idx) => (
                <tr
                  key={team.id}
                  className={`hover:bg-gray-50 transition-colors ${team.disqualified ? 'opacity-60' : idx < 2 ? 'bg-green-50/50' : ''}`}
                  data-testid={`standing-row-${team.team_id}`}
                >
                  <td className="pl-2 sm:pl-3 pr-1 py-2.5 sm:py-3 align-middle">
                    <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                      {/* Mobile: logo if present, else position badge fallback.
                          Desktop (sm+): position badge + logo side-by-side. */}
                      {team.team_logo ? (
                        <img
                          src={team.team_logo}
                          alt=""
                          className="flex-shrink-0 sm:hidden w-6 h-6 rounded-full object-cover ring-1 ring-gray-200"
                        />
                      ) : (
                        <span
                          className={`flex-shrink-0 sm:hidden inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${team.disqualified ? 'bg-red-100 text-red-600' : getPositionColor(team.position)}`}
                        >
                          {team.disqualified ? '–' : team.position}
                        </span>
                      )}
                      <span
                        className={`hidden sm:inline-flex flex-shrink-0 items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${team.disqualified ? 'bg-red-100 text-red-600' : getPositionColor(team.position)}`}
                      >
                        {team.disqualified ? '–' : team.position}
                      </span>
                      {team.team_logo && (
                        <img src={team.team_logo} alt="" className="flex-shrink-0 hidden sm:block w-6 h-6 rounded-full object-cover" />
                      )}
                      <span className="font-medium text-gray-800 leading-tight break-words">
                        {team.team_name}
                        {team.pending_decider && (
                          <span className="ml-1.5 align-middle text-[10px] font-bold bg-[#FEF8DC] text-[#2B3A67] border border-[#F4D341] px-1.5 py-0.5 rounded whitespace-nowrap"
                            data-testid={`standing-level-${team.team_id}`}>
                            Level<span className="hidden sm:inline"> — {deciderWord(team.pending_decider.method)} to decide</span>
                          </span>
                        )}
                        {team.walkovers > 0 && (
                          <span className="block text-[10px] font-normal text-gray-400">{team.walkovers} walkover{team.walkovers > 1 ? 's' : ''}</span>
                        )}
                        {team.disqualified && (
                          <span className="ml-1.5 align-middle text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded"
                            title={team.disqualification_mode === 'expunge' ? 'Disqualified — results removed' : 'Disqualified'}
                            data-testid={`standing-dq-${team.team_id}`}>DQ</span>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-1 sm:px-2 py-2.5 sm:py-3 text-center text-gray-600 tabular-nums">{team.played}</td>
                  <td className="px-1 sm:px-2 py-2.5 sm:py-3 text-center text-green-600 font-medium tabular-nums">{team.won}</td>
                  <td className="px-1 sm:px-2 py-2.5 sm:py-3 text-center text-red-600 tabular-nums">{team.lost}</td>
                  <td className="px-1 sm:px-2 py-2.5 sm:py-3 text-center text-gray-500 tabular-nums">{team.tied}</td>
                  <td className="px-1 sm:px-2 py-2.5 sm:py-3 text-center text-gray-500 tabular-nums">{team.no_result}</td>
                  <td className="px-1 sm:px-2 py-2.5 sm:py-3 text-center font-bold text-[#2B3A67] tabular-nums">{fmtPoints(team.points)}</td>
                  <td className={`pl-1 pr-2 sm:pr-3 py-2.5 sm:py-3 text-right font-mono text-[10px] sm:text-xs tabular-nums whitespace-nowrap ${
                    team.net_run_rate >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatNRR(team.net_run_rate)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Level teams waiting on a super over / bowl-out / toss */}
          {[...new Map(teams.filter((t) => t.pending_decider).map((t) => [t.pending_decider.team_ids.join(','), t.pending_decider])).values()].map((p) => (
            <div key={p.team_ids.join(',')} className="px-4 py-2.5 bg-[#FEF8DC] border-t border-[#F4D341]/60 text-xs text-[#2B3A67] flex items-center justify-between gap-3"
              data-testid="standings-decider">
              <span>
                <strong>{teams.filter((t) => p.team_ids.includes(t.team_id)).map((t) => t.team_name).join(' and ')}</strong> are level.
                The rulebook settles it with a {deciderWord(p.method)}.
              </span>
              {isAdmin && (
                <button onClick={() => setDeciding(p)} className="shrink-0 h-9 px-3 rounded-lg bg-[#2B3A67] text-white font-bold"
                  data-testid="record-decider">Record result</button>
              )}
            </div>
          ))}

          {/* Qualification Info */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
            <span className="inline-block w-3 h-3 bg-green-100 rounded mr-1"></span>
            Teams in qualification zone
            {teams.some((t) => t.disqualified) && (
              <span className="block mt-1" data-testid="standings-dq-note">
                <span className="font-bold text-red-600">DQ</span> disqualified — can't qualify
                {teams.some((t) => t.disqualification_mode === 'expunge') && '; results of teams marked as removed don\'t count for anyone'}
              </span>
            )}
            {pointAdjustments.filter((a) => teams.some((t) => t.team_id === a.team_id)).length > 0 && (
              <span className="block mt-1" data-testid="standings-adjustments">
                <span className="font-bold text-gray-600">Points adjusted:</span>{' '}
                {pointAdjustments.filter((a) => teams.some((t) => t.team_id === a.team_id))
                  .map((a) => `${a.team_name} ${a.amount > 0 ? '+' : ''}${a.amount} (${a.reason})`).join('; ')}
              </span>
            )}
          </div>
        </div>
      ))}
      <DeciderSheet open={!!deciding} pending={deciding} eventId={eventId} teams={standings}
        onClose={() => setDeciding(null)} onSaved={async () => { setDeciding(null); if (onRefresh) await onRefresh(); }} />
    </div>
  );
}
